import React, { useState } from "react";
import { MenuItem } from "@mui/material";
import { ContentURL, getContentUrl } from "../../Utils/ContentURL";
import { useAppState } from "../../../Redux/ReduxHooks";
import { Dispatch } from "../../Dispatch";
import { PaymentOption, PaymentOptionKind } from "../../Payment/PaymentEntities";
import { CheckForCardExpiry } from "../../Payment/PaymentCardValidation";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { GetRemoveCardDialogInput } from "./ConfirmationDialog/ShowConfirmationDialog";
import { ToasterType, WellknownErrorMessages } from "./Notifications/NotificationEntities";
import moment from "moment";
import { Api } from "../../../Services/Api";
import appstore from "../../../appStore";
import "./ExistingCardDropdownEntry.scss";

/** 
 * Renders the existing card entry in the payment method dropdown for Phone to CNP bookings.
 * Handles two scenarios:
 *   1. A card already attached to this booking (bookingDetails.Payment) - shown for all users.
 *   2. A previously saved card for a return user (bookingDetails.SavedCard) - shown when no booking-level card exists.
 * Reuses existing card rendering pattern from CardSelector.
 */
export const ExistingCardDropdownEntry: React.FC = () => {

    const bookingDetails = useAppState(state => state.PhoneToCnp.Booking);
    const isPaymentSystemAvailable = useAppState(i => i.payment.IsPaymentSystemAvailable);
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    // No booking details loaded yet
    if (!bookingDetails) return null;

    // Payment system not available
    if (!isPaymentSystemAvailable) return null;

    // Prefer the card already attached to this booking; fall back to the return user's saved card.
    // Payment and SavedCard are mutually exclusive: backend only populates SavedCard when Payment is null.
    const payment = bookingDetails.Payment ?? bookingDetails.SavedCard;

    // No card to display (neither an attached card nor a saved card for a return user)
    if (!payment) return null;

    // Create a PaymentOption from the existing card details to reuse existing logic
    const existingCardOption: PaymentOption = {
        Id: payment.CardId,
        Name: payment.CardNumber, // Show masked card number in the preselection (e.g. ****1234) instead of the card nickname
        Type: payment.CardType,
        IsDefault: false,
        Kind: PaymentOptionKind.CardOrWallet,
        Card: {
            CardId: payment.CardId,
            CardNumber: payment.CardNumber,
            CardType: payment.CardType,
            CardName: payment.CardName,
            CardExpiry: payment.CardExpiry,
            IsDefault: false,
            IsBlocked: false,
            CheckFor3Ds: false // 3DS disabled for existing cards in phone to CNP flow
        }
    };

    // Check if card is expired using the same logic as CheckForCardExpiry
    const expiresAt = moment(payment.CardExpiry, "MM/YY").add(1, "month");
    const atNow = moment();
    const isExpired = expiresAt.isBefore(atNow);

    // Get dialog input configuration 
    const removeCardDialogInput = GetRemoveCardDialogInput();

    return (
        <>
            <MenuItem 
                className="cardMenuItem" 
                value={payment.CardId} 
                disableGutters={true}
                onClick={() => OnExistingCardSelected(existingCardOption)}
            >
                <img 
                    className="cardSelectorCardImage" 
                    src={getContentUrl(GetPaymentTypeIcon(payment.CardType))} 
                    alt={`${payment.CardType} icon`}
                />
                <span className="cardSelectorCardName">
                    {payment.CardNumber}
                    {isExpired && <span className="card-expired-text">Card expired</span>}
                </span>
                <span onClick={(e) => OnRemoveCardClicked(e, setShowRemoveConfirmation)} className="remove-card-button" title="Remove card" >
                    <img src={getContentUrl(ContentURL.images.PhoneToCnp.Trash)}></img>
                </span>
            </MenuItem>
            
            {/* Reusable Confirmation Dialog - using pre-configured dialog input */}
            <ConfirmationDialog
                open={showRemoveConfirmation}
                onClose={() => setShowRemoveConfirmation(false)}
                title={removeCardDialogInput.title}
                message={removeCardDialogInput.message}
                cancelButtonText={removeCardDialogInput.cancelButtonText}
                confirmButtonText={removeCardDialogInput.confirmButtonText}
                onConfirm={() => OnConfirmRemoveCard(payment.CardId, setShowRemoveConfirmation, setIsRemoving)}
                confirmButtonColor={removeCardDialogInput.confirmButtonColor}
            />
        </>
    );
}

/** Event handler for clicking on the existing card menu item. */
function OnExistingCardSelected(cardOption: PaymentOption) {
    Dispatch.Payment.ClearCardError();
    Dispatch.UILogicControl.CloseCardSelectorDropdown();
    Dispatch.PhoneToCnp.PaymentOption(cardOption);
    
    // Check if the card is expired and display error if needed
    CheckForCardExpiry(cardOption);
}

/** Event handler for clicking on the remove button. Opens the confirmation dialog. */
function OnRemoveCardClicked(e: React.MouseEvent, setShowRemoveConfirmation: (show: boolean) => void) {
    e.stopPropagation(); // Prevent the card from being selected when clicking remove
    setShowRemoveConfirmation(true);
}

/** 
 * Event handler for confirming card removal. Executes the actual removal logic.
 * Works for both Payment (attached booking card) and SavedCard (return user's stored card) —
 * RemoveExistingCard clears both fields since only one is ever set at a time.
 * The ServiceManager automatically adds BackingUser.Auth to API requests.
 */
async function OnConfirmRemoveCard(
    cardId: string,
    setShowRemoveConfirmation: (show: boolean) => void,
    setIsRemoving: (isRemoving: boolean) => void
) {
    setIsRemoving(true);

    // Ensure BackingUser is set from the SavedCardGuestAuth stored in the PhoneToCnp slice.
    // This guarantees the DeleteCard request uses the correct guest account credentials even if
    // GuestPayment.BackingUser was cleared or overwritten by a subsequent payment selection.
    // This auth is exclusively stored for the phone-to-CNP flow (set in PhoneBookingLinkHandler).
    const savedCardGuestAuth = appstore.getState().PhoneToCnp.SavedCardGuestAuth;
    if (savedCardGuestAuth) {
        Dispatch.GuestPayment.TempUserDetails(savedCardGuestAuth);
    }

    // Call the backend API to delete the card
    // ServiceManager automatically adds BackingUser.Auth header from state.GuestPayment.BackingUser
    const removeCardResponsePayload = await Api.Payment.DeleteCard(cardId);
    
    // Only update local state if the API call was successful
    if (removeCardResponsePayload.isSuccess) {
        Dispatch.PhoneToCnp.RemoveExistingCard();
        // Clear the stale BackingUser credentials — if not cleared, PendingCreditCardGuest() will return
        Dispatch.GuestPayment.ClearTempUser();
        Dispatch.UILogicControl.CloseCardSelectorDropdown();
        Dispatch.PhoneToCnp.ShowToaster(ToasterType.SavedCardRemoved);
    } else {
        // Inform the user that card deletion failed so they are not left thinking the card was removed.
        Dispatch.PhoneToCnp.ShowErrorMessage(WellknownErrorMessages.RemoveCardFailed);
    }
    
    setIsRemoving(false);
    setShowRemoveConfirmation(false);
}

/** Get the icon URL for the payment card type.
 *  Tries exact match first, then title-cased (e.g. "MasterCard" → "Mastercard") to handle
 *  backend CardType formats that differ in case from the selector keys. */
function GetPaymentTypeIcon(cardType: string): string {
    const selector = ContentURL.images.PaymentType.Selector as Record<string, string>;
    const titleCased = cardType ? cardType.charAt(0).toUpperCase() + cardType.slice(1).toLowerCase() : '';
    return selector[cardType] || selector[titleCased] || selector.CreditOrDebit;
}
